import React, { useState, useEffect } from "react"
import { useHistory } from "react-router-dom"
import firebase from "firebase"
import moment from "moment"

import { db } from "../../utils/firebase"
import { AMENDMENT_CATEGORIES, AMENDMENT_STATUSES } from "../../utils/exports/amendments"

import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Table from "../../components/structure/table/table"
import Button from "../../components/ui/button/button"

import NewAmendmentWindow from "./_components/new-amendment"
import "./amendments.scss"

export default function Amendments() {
	const history = useHistory()
	const [amendments, setAmendments] = useState([])
	const [loading, setLoading] = useState(true)
	const [showNew, setShowNew] = useState(false)
	const [bookingsById, setBookingsById] = useState({})

	useEffect(() => {
		const unsub = db
			.collection("amendments")
			.orderBy("createdAt", "desc")
			.onSnapshot(
				(snap) => {
					setAmendments(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
					setLoading(false)
				},
				(err) => {
					console.error("Unable to load amendments", err)
					setLoading(false)
				},
			)
		return () => unsub()
	}, [])

	// Pull the live `reference` field off each linked booking so the table
	// shows the canonical reference even for amendments whose denormalised
	// `bookingReference` was stored as the doc id (pre-prefix bug fix).
	useEffect(() => {
		const missing = Array.from(
			new Set(
				amendments
					.map((a) => a.bookingId)
					.filter((id) => id && !bookingsById[id]),
			),
		)
		if (missing.length === 0) return

		let cancelled = false
		const load = async () => {
			const next = {}
			const batchSize = 10
			for (let i = 0; i < missing.length; i += batchSize) {
				const batch = missing.slice(i, i + batchSize)
				const docs = await db
					.collection("bookings")
					.where(firebase.firestore.FieldPath.documentId(), "in", batch)
					.get()
				docs.forEach((doc) => {
					next[doc.id] = doc.data()
				})
			}
			if (cancelled) return
			setBookingsById((prev) => ({ ...prev, ...next }))
		}
		load().catch((err) => console.error("Unable to load bookings for amendments table", err))
		return () => {
			cancelled = true
		}
	}, [amendments, bookingsById])

	const bookingReferenceFor = (a) => {
		const booking = bookingsById[a.bookingId]
		if (booking?.reference) return `GHDB-${booking.reference}`
		// Booking doc loaded but has no reference field — show a dash rather
		// than the doc id so the column is always meaningful.
		if (booking) return "—"
		return null
	}

	const formatCreated = (entry) => {
		const seconds = entry.createdAt?.seconds || entry.createdAt?._seconds
		if (!seconds) return "—"
		return moment(seconds, "X").format("MMM D, YYYY HH:mm")
	}

	const renderAwaitingFlags = (a) => {
		const parts = []
		if (a.awaitingClient) parts.push("Client")
		if (a.awaitingSupplier) parts.push("Supplier")
		return parts.length ? `Awaiting: ${parts.join(", ")}` : ""
	}

	return (
		<Tile fullPage={true}>
			<Title className="flex">
				<h1>Amendments</h1>
				<Button
					small={true}
					label="New amendment"
					onClick={() => setShowNew(true)}
				/>
			</Title>

			<Table
				className="amendments-table"
				headings={[
					"Reference",
					"Booking",
					"Category",
					"Summary",
					"Status",
					"Awaiting",
					"Created",
				]}>
				{loading && (
					<tr>
						<td colSpan={7}>
							<small>Loading amendments…</small>
						</td>
					</tr>
				)}

				{!loading && amendments.length === 0 && (
					<tr>
						<td colSpan={7}>
							<small>No amendments yet. Click "New amendment" to create the first one.</small>
						</td>
					</tr>
				)}

				{!loading &&
					amendments.map((a) => (
						<tr
							key={a.id}
							className="amendments-row"
							onClick={() => history.push(`/amendments/${a.id}`)}>
							<td>{a.reference}</td>
							<td>
								<a
									href={`/booking/${a.bookingId}`}
									target="_blank"
									rel="noopener noreferrer"
									onClick={(e) => e.stopPropagation()}>
									{bookingReferenceFor(a) ?? <small>Loading…</small>}
								</a>
							</td>
							<td>
								{AMENDMENT_CATEGORIES[a.category] || a.category}
								{a.category === "other" && a.categoryOther ? ` — ${a.categoryOther}` : ""}
							</td>
							<td>{a.summary}</td>
							<td>{AMENDMENT_STATUSES[a.status] || a.status}</td>
							<td>
								<small>{renderAwaitingFlags(a)}</small>
							</td>
							<td>{formatCreated(a)}</td>
						</tr>
					))}
			</Table>

			{showNew && <NewAmendmentWindow close={() => setShowNew(false)} />}
		</Tile>
	)
}
